import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface ReviewTalentsSummaryData {
  total_talents: number;
  average_score: number;
  interview_ready: number;
  probe_further: number;
  rejected: number;
  status: string;
  deadline?: string | null;
  bucket_summary?: Record<
    string,
    {
      decision: string;
      from: number;
      to: number;
      count: number;
    }
  >;
}

interface ReviewTalentsSummaryResponse {
  message: string;
  data: ReviewTalentsSummaryData;
}

/**
 * Hook for fetching review talents dashboard summary
 */
export const useReviewTalentsSummary = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviewTalentsSummary', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<ReviewTalentsSummaryResponse>(
        '/candidate/dashboard/summary/',
        {
          params: { assessment_id: assessmentId },
        }
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch summary';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    // Reset error flag when error is cleared
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    summary: data,
    isLoading,
    error,
    refetch,
  };
};
